import braintree from 'braintree-web';
import classNames from 'classnames';
import { useAtomValue, useSetAtom } from 'jotai';
import { observer } from 'mobx-react-lite';
import type { FlowType } from 'paypal-checkout-components';
import { type HTMLProps, useEffect, useState } from 'react';

import { useAnalytics } from '@features/Analytics';
import { createAppLovinSubscriptionPlanPayload } from '@features/Analytics/utils';
import { useFeature } from '@features/FeatureToggle';
import * as payments from '@features/Paywall/api/payments';
import { selectedPlanAtom, userPaymentsInfoAtom } from '@features/Stripe/atoms';
import LoadingIndicator from 'src/components/common/loading-indicator/loading-indicator';
import { useStores } from 'src/components/common/root-store-provider/root-store-provider';
import type { SubscriptionPlan } from 'src/models/subscription-plan/subscription-plan';
import {
  AnalyticsPaymentMethod,
  AnalyticsPaymentSystem,
  handleCheckoutOpenRequest,
} from 'src/utils/payment/payment-analytics';
import {
  PaymentCompletionStatus,
  usePaymentCompletionStatus,
} from 'src/utils/payment/payment-completion-status';

import PaypalLogo from '../../../../assets/images/payment/paypal-logo.svg';

import styles from './paypal-payment-button.module.scss';

const PAYPAL_BUTTONS_CONTAINER_ID = 'paypal-buttons-container';

const PaypalPaymentButton = observer(
  ({
    className,
    subscriptionPlan,
    discount = false,
    onLoadEnd,
    ...props
  }: {
    onLoadEnd: () => void;
    subscriptionPlan: SubscriptionPlan;
    discount?: boolean;
  } & Omit<HTMLProps<HTMLDivElement>, 'children'>) => {
    const { authStore, paymentsStore } = useStores();
    const { user, auth_token } = authStore;
    const { introOfferData } = useAtomValue(selectedPlanAtom);
    const setPaymentInfo = useSetAtom(userPaymentsInfoAtom);
    const {
      trackFacebookEvent,
      trackGoogleEvent,
      trackPinterestEvent,
      trackAppLovin,
    } = useAnalytics();
    const fbPixelSept2025Feature = useFeature('fbPixelSept2025Feature');
    const { setPaymentCompletionStatus } = usePaymentCompletionStatus();

    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
      setIsLoading(true);
      (async () => {
        if (!user) {
          throw new Error('A user is required');
        }

        const clientToken = await payments.generateBraintreeClientToken({
          customerEmail: user.email,
          customerId: user.serverId.toString(),
          merchantAccountId: subscriptionPlan.merchantAccountId,
        });
        const client = await braintree.client.create({
          authorization: clientToken,
        });

        const paypalCheckout = await braintree.paypalCheckout.create({
          client,
        });
        await paypalCheckout.loadPayPalSDK({ vault: true });
        const buttons = window.paypal.Buttons({
          style: { height: 55 },
          // @ts-ignore (an issue with library typings)
          fundingSource: window.paypal.FUNDING.PAYPAL,
          createBillingAgreement: () => {
            handleCheckoutOpenRequest({
              subscriptionPlan,
              paymentMethod: AnalyticsPaymentMethod.PAYPAL,
              paymentSystem: AnalyticsPaymentSystem.BRAINTREE,
              email: user!.email,
            });
            !fbPixelSept2025Feature &&
              trackFacebookEvent({
                eventName: 'AddPaymentInfo',
                customData: {
                  paymentSystem: 'braintree',
                  paymentMethod: 'paypal',
                },
                options: {
                  email: user?.email,
                },
              });
            trackPinterestEvent({ eventName: 'AddToCart' });
            trackAppLovin({
              eventName: 'add_payment_info',
              options: createAppLovinSubscriptionPlanPayload({
                subscriptionPlan,
                paymentMethod: 'paypal',
                paymentSystem: 'braintree',
                userEmail: user?.email,
                userId: user?.serverId,
              }),
            });
            if (discount) {
              trackGoogleEvent({ eventName: 'offer_onetime_tapped' });
            }
            return paypalCheckout.createPayment({
              flow: 'vault' as (typeof FlowType)['Vault'],
            });
          },
          onCancel: () => {
            paymentsStore.setWasPaymentClosed(true);
            trackGoogleEvent({
              eventName: 'add_payment_info_skipped',
              options: {
                payment_system: 'braintree',
                payment_method: AnalyticsPaymentMethod.PAYPAL,
              },
            });
          },
          onApprove: async (data) => {
            setIsLoading(true);
            try {
              const authorizationResponse =
                await paypalCheckout.tokenizePayment(data);
              const { bt_subscription_id, customer_id } =
                await payments.createBraintreeSubscription({
                  customerId: user!.serverId.toString(),
                  customerToken: auth_token!,
                  merchantAccountId: subscriptionPlan.merchantAccountId,
                  planId: subscriptionPlan.braintreePlanId!,
                  paymentMethodNonce: authorizationResponse.nonce,
                  btIntroOfferAmount: `${introOfferData?.fullPriceDiscount}`,
                  btIntroOfferCurrency: introOfferData?.currency,
                });
              setPaymentInfo((prev) => ({
                ...prev,
                btSubscriptionId: bt_subscription_id,
                btCustomerId: customer_id,
                paymentMethod: 'paypal',
                paymentSystem: 'braintree',
              }));
              setPaymentCompletionStatus(PaymentCompletionStatus.SUCCESS);
              return authorizationResponse;
            } finally {
              setIsLoading(false);
            }
          },
          onError: () => {
            setPaymentCompletionStatus(PaymentCompletionStatus.FAILURE);
          },
        });
        if (document.querySelector(`#${PAYPAL_BUTTONS_CONTAINER_ID}`)) {
          buttons.render(`#${PAYPAL_BUTTONS_CONTAINER_ID}`);
        }

        setIsLoading(false);
        onLoadEnd();
      })();
    }, [
      auth_token,
      setPaymentInfo,
      discount,
      fbPixelSept2025Feature,
      introOfferData?.currency,
      introOfferData?.fullPriceDiscount,
      onLoadEnd,
      paymentsStore,
      setPaymentCompletionStatus,
      subscriptionPlan,
      trackAppLovin,
      trackFacebookEvent,
      trackGoogleEvent,
      trackPinterestEvent,
      user,
    ]);

    return (
      <div className={classNames(styles.container, className)} {...props}>
        {isLoading ? (
          <LoadingIndicator />
        ) : discount ? (
          'GET MY DISCOUNT'
        ) : (
          <PaypalLogo />
        )}
        <div className={styles.frame} id={PAYPAL_BUTTONS_CONTAINER_ID} />
      </div>
    );
  },
);

export default PaypalPaymentButton;
